import { useId, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/**
 * Last assessment's comment on an item, for reference beside the assessor's
 * own. It is never their comment: they write their own, adding this to
 * it or copying it if they choose.
 *
 * The text keeps a fixed height and scrolls within it, so a long comment never
 * moves the page, and it takes focus so the keyboard can scroll it too.
 */
export const PreviousComment = ({
  comment,
  finishedAt,
  canAdd,
  onAdd,
  onCopy,
}: {
  comment: string;
  /** When the assessment it came from was approved or completed. */
  finishedAt: Date;
  /** Whether the assessor's own comment can be written to yet. */
  canAdd: boolean;
  onAdd: () => Promisable<void>;
  /** Puts the comment on the clipboard, rejecting where the browser refuses. */
  onCopy: (comment: string) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const captionId = useId();
  const [copy, setCopy] = useState<"copied" | "failed">();

  const copyComment = async () => {
    try {
      await onCopy(comment);
      setCopy("copied");
    } catch {
      setCopy("failed");
    }
  };

  return (
    <Box>
      <Typography
        id={captionId}
        variant="caption"
        color="text.secondary"
        component="div"
      >
        {t("assessment.item.previousComment.caption", {
          date: CALENDAR_DATE.format(finishedAt),
        })}
      </Typography>
      <Box
        role="region"
        aria-labelledby={captionId}
        tabIndex={0}
        sx={{
          height: "6.5em",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          px: 1.5,
          py: 1,
          bgcolor: "action.hover",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">{comment}</Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button
          size="small"
          disabled={!canAdd}
          onClick={() => {
            void onAdd();
          }}
        >
          {t("assessment.item.previousComment.add")}
        </Button>
        <Button
          size="small"
          onClick={() => {
            void copyComment();
          }}
        >
          {t("assessment.item.previousComment.copy")}
        </Button>
        {/* Said either way: a copy that silently failed would leave whatever
            was on the clipboard before to be pasted in its place. */}
        <Typography
          role="status"
          variant="caption"
          color={copy === "failed" ? "error" : "text.secondary"}
          sx={{ alignSelf: "center" }}
        >
          {copy === undefined
            ? null
            : t(`assessment.item.previousComment.${copy}`)}
        </Typography>
      </Stack>
    </Box>
  );
};
